/**
 * Data layer for the learning module.
 *
 * Every call degrades the same way the backend does: a disabled module returns
 * 404, an unreachable store returns 502, and both surface as "nothing here"
 * rather than an error screen. Review is a side activity — it must never look
 * broken just because the feature is off.
 */
import { authFetch } from "../../utils/auth";

const BASE = "/api/learning";
const HTTP_UNAVAILABLE = 503;
const HTTP_NOT_FOUND = 404;

/** An Error carrying the server's reason as `message` and the HTTP status as `status`. */
const requestError = (message, status) => Object.assign(new Error(message), { status });

/** True when a call failed because the learning store is unreachable (503): a "not connected" state, not a failure. */
export const isUnavailable = (error) => Boolean(error) && error.status === HTTP_UNAVAILABLE;

async function call(path, options) {
  const res = await authFetch(`${BASE}${path}`, options);
  if (res.status === HTTP_NOT_FOUND) return null; // module disabled — not an error
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.message;
    const msg = typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw requestError(msg, res.status);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Trails                                                             */
/* ------------------------------------------------------------------ */

export const listTrails = (status = "draft", { search } = {}) => {
  const params = new URLSearchParams({ status });
  if (search) params.set("search", search);
  return call(`/trails?${params.toString()}`);
};

export const getTrail = (id) => call(`/trails/${id}`);

// The trail for a live session, keyed on the session id rather than the pk.
// Pure read of the stored trail — the per-turn capture keeps it current, so the
// symptom this returns is the engine's normalised statement of the problem and
// moves with the diagnosis. Use it anywhere the RAW first message would
// otherwise be shown as the symptom.
export const getTrailBySession = (sessionId) =>
  call(`/trails/by-session/${encodeURIComponent(sessionId)}`);

// A single string, not the whole trail — for UI that only needs the symptom
// and cannot afford the full trail's three-statement assembly (~1-2s measured
// in prod). One indexed SELECT server-side, plus an in-memory cache here so
// the SAME session never pays the network round trip twice in one page load —
// the correction composer, opened repeatedly from the same live chat, goes
// from a ~1-2s wait to instant on every open after the first.
//
// Stale-while-revalidate: callers use getSessionSymptomCached() for instant
// render of whatever the cache holds, then await getSessionSymptom() which
// ALWAYS hits the network (deduped) and returns the fresh value. The cache is
// updated on every successful fetch, so the next open is instant again.
const _symptomCache = new Map();

/**
 * Synchronous read of the last-resolved symptom for a session, or null.
 * Use this for instant initial render — it never triggers a fetch.
 */
export const getSessionSymptomCached = (sessionId) => {
  if (!sessionId) return null;
  const cached = _symptomCache.get(sessionId);
  // Only return resolved data, not in-flight promises.
  return cached && typeof cached.then !== "function" ? cached : null;
};

export const getSessionSymptom = async (sessionId) => {
  if (!sessionId) return null;

  const inflight = _symptomCache.get(sessionId);
  // A Promise means a fetch is already in flight — dedupe by returning it.
  if (inflight && typeof inflight.then === "function") return inflight;

  const pending = call(`/trails/symptom-by-session/${encodeURIComponent(sessionId)}`)
    .then((data) => {
      _symptomCache.set(sessionId, data);
      return data;
    })
    .catch((err) => {
      // A failure must not poison the cache — remove the promise, but keep
      // any previously resolved value so the next caller still renders
      // instantly while retrying.
      const prev = _symptomCache.get(sessionId);
      if (prev === pending) _symptomCache.delete(sessionId);
      throw err;
    });

  // Cache the promise so concurrent callers share this single request.
  _symptomCache.set(sessionId, pending);
  return pending;
};

export const updateTrail = (id, patch) =>
  call(`/trails/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

/**
 * @deprecated — use updateTrail instead. Kept for TrailReview compat.
 *
 * `notes` ({ [stepNo]: text }) is OPTIONAL and only reaches the wire when it
 * holds something: a backend that predates ruled-out notes then receives
 * byte-for-byte the payload it receives today. An empty string is a real
 * value — it is how the engineer clears a note — so only an empty MAP is
 * dropped, never an empty entry.
 */
export const markSteps = (id, results, notes) =>
  call(`/trails/${id}/steps`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      notes && typeof notes === "object" && Object.keys(notes).length
        ? { results, notes }
        : { results }
    ),
  });

export const deleteStep = (id, stepNo) =>
  call(`/trails/${id}/steps/${stepNo}`, { method: "DELETE" });

export const reorderSteps = (id, order) =>
  call(`/trails/${id}/steps/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order }),
  });

export const submitTrail = (id, outcome) =>
  call(`/trails/${id}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outcome }),
  });

export const approveTrail = (id) =>
  call(`/trails/${id}/approve`, { method: "POST" });

export const rejectTrail = (id, note) =>
  call(`/trails/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });

/** @deprecated alias — backend also accepts POST /trails/{id}/discard. */
export const publishTrail = (id, outcome) => submitTrail(id, outcome);

export const discardTrail = (id) =>
  call(`/trails/${id}/discard`, { method: "POST" });

/* ------------------------------------------------------------------ */
/*  Corrections                                                        */
/* ------------------------------------------------------------------ */

export const reportCorrection = (payload) =>
  call("/corrections", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const listCorrections = (status) => {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return call(`/corrections${qs}`);
};

export const updateCorrection = (id, patch) =>
  call(`/corrections/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

/* ------------------------------------------------------------------ */
/*  Learning editor save (patterns are learned at once on submit)      */
/* ------------------------------------------------------------------ */

// Writes keep the server's reason: a refusal answers with a short code as `detail` (already_decided, not_in_diagnosis,
// item_gone, ...), and a 404 here means "refused", not "feature off", so these do not go through `call`.
async function act(path, body, method = "POST") {
  const res = await authFetch(`${BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return answerOf(res);
}

/** The body of a response, or an Error carrying the server's reason (`detail`, or `detail.message`) and the HTTP status. */
async function answerOf(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    const msg = typeof detail === "string" ? detail
      : (detail && typeof detail.message === "string" ? detail.message : `Request failed (${res.status})`);
    throw Object.assign(requestError(msg, res.status), { issues: Array.isArray(data.issues) ? data.issues : [] });
  }
  return data;
}

// Reads whose 404 carries a reason (item_gone, ...) rather than meaning "feature off": the reason reaches the caller.
const read = async (path, { signal } = {}) => answerOf(await authFetch(`${BASE}${path}`, { signal }));

/** True when a read failed with 404 (the item is gone, or the endpoint is missing: the reason tells which). */
export const isNotFound = (error) => Boolean(error) && error.status === HTTP_NOT_FOUND;

// An engineer saves what they wrote in the Learning editor: {session_id, category, symptoms, resolution_steps, root_cause}.
// The pattern is learned at once (no SME approval); the Learning Catalog can undo it.
export const saveReviewPattern = (payload) => act("/review-items", payload);

// Preview-match: how each line of a draft pattern matches the existing catalog, so the engineer can choose reuse or new:
// {status, pattern, symptoms, steps, cause, issues, meta}. Returns null when the endpoint is missing. Nothing is written.
export const previewMatch = (sessionId, payload, signal) =>
  call("/review-items/preview-match", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, session_id: sessionId }),
    signal,
  });

// What the editor starts with for this chat: {category, symptoms, steps, root_cause, fix, truncated, turn}, each line
// {text, source, origin, ref_id, hedged}. Rejects on 404 (not_in_diagnosis), 503 and network errors: the caller falls back to the local diagnosis state.
export const getPrefill = (sessionId, { signal } = {}) => read(`/sessions/${encodeURIComponent(sessionId)}/prefill`, { signal });

// Eligibility: whether the editor can save for this session. Returns {can_save, reason} or null.
export const checkEligibility = (sessionId) =>
  call(`/review-items/eligibility?session_id=${encodeURIComponent(sessionId)}`);

/* ------------------------------------------------------------------ */
/*  Learning Catalog                                                   */
/* ------------------------------------------------------------------ */

const CATALOG_FILTER_KEYS = Object.freeze(["source", "product", "category", "problem", "keyword", "symptom", "state", "limit", "offset"]);
const CATALOG_OPTION_KEYS = Object.freeze(["source", "state"]);

/** `?key=value&…` of the non-empty `keys` of `values` ("" when none). */
const queryOf = (keys, values = {}) => {
  const params = new URLSearchParams();
  keys.forEach((key) => {
    const value = values[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") params.set(key, String(value).trim());
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

// One page of catalog entries of one source (the page lists manual ones), newest first: {items, total, limit, offset}.
// product / category are the products-table slugs. Empty filters are left off the query. null = not connected. Reads take an
// optional AbortSignal so a superseded request is cancelled instead of racing the current one.
export const listCatalog = (filters = {}, { signal } = {}) => call(`/catalog${queryOf(CATALOG_FILTER_KEYS, filters)}`, { signal });

// The product -> category dropdowns from the products tables, with entry counts of that source and state:
// {products: [{product: {id, name, slug}, categories: [{slug, name, count}]}], unmatched}.
export const catalogFilters = ({ source, state } = {}, { signal } = {}) =>
  call(`/catalog/filters${queryOf(CATALOG_OPTION_KEYS, { source, state })}`, { signal });

// Edit a pattern: {symptoms, resolution_steps, root_cause, fix_kind?, fix_text?}. Leaving both fix fields out keeps the fix.
export const editCatalogPattern = (id, pattern) => act(`/catalog/${encodeURIComponent(id)}`, pattern, "PATCH");

export const undoLearning = (id) => act(`/catalog/${encodeURIComponent(id)}/undo`);

export const relearnPattern = (id) => act(`/catalog/${encodeURIComponent(id)}/relearn`);

/* ------------------------------------------------------------------ */
/*  Learning history (every learned case; undo / redo)                 */
/* ------------------------------------------------------------------ */

const HISTORY_FILTER_KEYS = Object.freeze(["product", "category", "cause", "kind", "status", "date_from", "date_to", "q", "manual",
  "limit", "offset"]);
const historyPath = (eid, action) => `/history/${encodeURIComponent(eid)}/${action}`;

// One page of learning events, newest first: {total, all, limit, offset, items, causes}. `causes` is the per-cause summary
// (cases, trust percent, category). Empty filters are left off the query. null = not connected.
export const listHistory = (filters = {}, { signal } = {}) => call(`/history${queryOf(HISTORY_FILTER_KEYS, filters)}`, { signal });

// The server's choices and labels: {kinds, undo_reasons, statuses}, each [{key, label}].
export const historyOptions = ({ signal } = {}) => call("/history/options", { signal });

// What undoing (or redoing, for an undone case) would do to its cause: {action, cause_label, cases_now, cases_after,
// percent_now, percent_after, change_points, small_effect, ...}. Read-only.
export const previewHistory = (eid) => call(historyPath(eid, "preview"));

// {result: "done" | "nothing_to_do", eid, cause_id}
export const undoHistory = (eid, reason) => act(historyPath(eid, "undo"), { reason });

export const redoHistory = (eid) => act(historyPath(eid, "redo"));

/* ------------------------------------------------------------------ */
/*  Session context (shown at the top of the catalog's detail popups)  */
/* ------------------------------------------------------------------ */

// How a conversation started: {session_id, first_query: {text, at} | null, context: [{key, label, value}], started_by}.
// `value` is a string or a string[]; the labels are the server's. A 404 `item_gone` means the conversation is no longer there.
export const getSessionContext = (sessionId, { signal } = {}) =>
  read(`/sessions/${encodeURIComponent(sessionId)}/context`, { signal });

/* ------------------------------------------------------------------ */
/*  Insight                                                            */
/* ------------------------------------------------------------------ */

// Where engineers abandoned the suggested path, most frequent first — the
// ranked list of places the documented runbooks fail a real engineer.
// Admin-only on the backend (GET /api/learning/overrides), same gate as the
// trail review routes.
export const listOverrideHotspots = (limit = 20) =>
  call(`/overrides?limit=${encodeURIComponent(limit)}`);
